// API client for making authenticated requests
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : 'https://api.loglimit.com');

export interface Project {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  platform: string | null;
  status: 'active' | 'archived';
  retention_days: number;
  created_at: number;
  updated_at?: number;
  active_key_count?: number;
  event_count?: number;
  last_event_at?: number | null;
  dsn?: string | null;
}

export interface McpAccessToken {
  id: string;
  token_prefix: string;
  name: string;
  scopes: string[];
  created_by: string;
  creator_email?: string;
  created_at: number;
  expires_at: number | null;
  last_used_at: number | null;
  revoked_at: number | null;
}

export interface CreatedMcpAccess {
  token: McpAccessToken;
  raw_token: string;
  endpoint: string;
  configurations: {
    codex_toml: string;
    claude_code: string;
    generic_json: string;
    smoke_command: string;
  };
}

export interface TransmissionApplication {
  id: string;
  organization_id: string;
  project_id: string;
  application_id: string;
  name: string;
  platform: string;
  status: 'active' | 'archived';
  release_count?: number;
  latest_release_at?: number | null;
  active_suppression_count?: number;
}

export interface TransmissionRelease {
  id: string;
  application_row_id: string;
  application_id?: string;
  application_name?: string;
  project_id?: string;
  version: string;
  build_id: string;
  config_version: number;
  callsite_count: number;
  active_suppression_count?: number;
  pending_recommendation_count?: number;
  created_at?: number;
}

export type TransmissionLogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

export interface RecentTransmissionLog {
  event_id: string | null;
  timestamp_ms: number | null;
  timestamp: string | null;
  received_at: number;
  received_at_iso: string;
  level: TransmissionLogLevel;
  message: string;
  attributes?: Record<string, unknown>;
  attributes_truncated?: boolean;
  origin: {
    project: { id: string; name: string; slug: string };
    application: { id: string; name: string };
    release: { id: string; version: string; build_id: string };
    service: string;
    instance_hash: string | null;
    callsite_id: string | null;
    runtime_index: number | null;
    batch_id: string;
  };
}

export interface RecentTransmissionLogsResponse {
  logs: RecentTransmissionLog[];
  page: {
    returned: number;
    matched_in_scanned_batches: number;
    scanned_batches: number;
    batch_scan_limit: number;
    has_more: boolean;
    next: { offset: number; cursor: string | null } | null;
    partial: boolean;
  };
  query: Record<string, unknown>;
  storage: Record<string, unknown>;
  source_resolution: Record<string, unknown>;
}

export interface TransmissionCallsite {
  runtime_index: number;
  callsite_id: string;
  template_hash: string;
  declared_level: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal' | null;
  recommendation_id: string | null;
  recommendation_type: 'classification' | 'carryover' | 'reclassification' | null;
  recommended_action: 'suppress' | 'allow' | 'review' | null;
  recommendation_reason: string | null;
  confidence: number | null;
  recommendation_status: string | null;
  model_version: string | null;
  source_release_id: string | null;
  source_callsite_id: string | null;
  suggested_level: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal' | null;
  semantic_type: 'diagnostic' | 'lifecycle' | 'business_event' | 'security' | 'performance' | 'dependency' | 'audit' | 'error' | 'unknown' | null;
  value_assessment: 'low' | 'medium' | 'high' | 'unknown' | null;
  decision_id: string | null;
  effective_action: 'suppress' | 'allow' | null;
  decided_by: string | null;
  decided_at: number | null;
  transmitted_count: number;
  transmitted_bytes: number;
  suppressed_attempts: number;
  suppressed_bytes_estimate: number;
  distinct_installations_estimate: number;
}

export interface TransmissionAuditEntry {
  id: string;
  callsite_id: string;
  actor_user_id: string;
  actor_email?: string;
  action: 'approve_suppression' | 'approve_allow' | 'reject' | 'dismiss' | 'revoke' | 'replace_manifest';
  recommendation_id: string | null;
  reason: string;
  created_at: number;
}

export interface RuntimeCapabilities {
  product: 'loglimit';
  protocol_version: 1;
  features: {
    transmission_control: { available: boolean };
    node_sdk: { available: boolean };
    hosted_mcp: { available: boolean };
    linux_security_monitoring: {
      available: boolean;
      enrollment: 'device_code';
      delivery: 'acknowledged_https';
      evidence_storage: 'r2';
      normalized_index: 'd1';
    };
    security_ai_analysis: { available: boolean };
    ai_recommendations: { available: boolean };
    realtime_invalidations: { available: boolean };
    event_stream: { available: boolean; storage_mode: 'pipeline' | 'r2_fallback' };
    topicbell_notifications: { available: boolean; connection_model: 'per_user_topic_token' };
    password_reset: { available: boolean };
    authorizeme_sso: { available: boolean };
  };
}

export interface ManagementApiKey {
  id: string;
  name: string;
  created_at: number;
  last_used_at: number | null;
  revoked_at: number | null;
}

export interface OrganizationConfig {
  retention_days: number;
  alert_email: string;
  alert_thresholds: { error_rate_per_minute: number; critical_count: number };
  data_regions: string[];
  cost_limit_usd_per_month: number;
  avoidance_cost_per_gib_usd: number | null;
}

export type NotificationEventType =
  | 'transmission_error_summary'
  | 'sensitive_data_alert'
  | 'security_alert'
  | 'pattern_analysis_complete';

export interface NotificationChannel {
  id: string;
  channel_type: 'topicbell';
  display_name: string;
  topic: string;
  credential_prefix: string;
  event_types: NotificationEventType[];
  enabled: boolean;
  last_verified_at: number | null;
  last_delivery_at: number | null;
  last_error: string | null;
  created_at: number;
  updated_at: number;
}

export interface NotificationChannelsResponse {
  channels: NotificationChannel[];
  providers: {
    topicbell: {
      available: boolean;
      api_base_url: string;
    };
  };
}

export interface SecurityDeviceAuthorizationResult {
  status: 'approved' | 'denied';
}

export interface SecurityAgentSummary {
  id: string;
  name: string;
  host_name: string;
  platform: string;
  architecture: string;
  agent_version: string;
  service: string;
  status: 'active' | 'degraded' | 'offline' | 'revoked';
  last_seen_at: number | null;
  last_batch_at: number | null;
  offline: boolean;
}

export interface SecurityAlertSummary {
  id: string;
  incident_id: string | null;
  detection_type: string;
  title: string;
  description?: string;
  severity: 'info' | 'warning' | 'high' | 'critical';
  status: 'open' | 'acknowledged' | 'resolved' | 'dismissed';
  source_ip: string | null;
  event_count: number;
  first_seen_at: number;
  last_seen_at: number;
}

export interface SecurityIncidentSummary {
  id: string;
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'high' | 'critical';
  status: 'open' | 'investigating' | 'contained' | 'resolved' | 'closed';
  source_ip: string | null;
  event_count: number;
  first_seen_at: number;
  last_seen_at: number;
}

export interface SecurityOverview {
  window: { from: number; to: number };
  totals: {
    events: number;
    alerts: number;
    open_alerts: number;
    incidents: number;
    open_incidents: number;
    agents: number;
    offline_agents: number;
  };
  alerts_by_severity: Array<{ severity: string; count: number }>;
  agents: SecurityAgentSummary[];
  recent_alerts: SecurityAlertSummary[];
}

export interface SecurityIncidentDetail {
  incident: SecurityIncidentSummary;
  alerts: SecurityAlertSummary[];
  evidence: Array<{
    id: string;
    event_id: string;
    timestamp: number;
    host: string;
    event_type: string;
    outcome: string;
    severity: string;
    source_ip: string | null;
    user_name: string | null;
    flagged: boolean;
    flag_reason: string | null;
  }>;
}

export type SecurityEventSeverity = 'info' | 'warning' | 'high' | 'critical';
export type SecurityEventOutcome = 'success' | 'failure' | 'unknown' | 'closed';

export interface SecurityEvent {
  id: string;
  event_id: string;
  agent_id: string;
  batch_row_id: string;
  schema_version: string;
  timestamp: number;
  observed_at: number;
  host: string;
  source: string;
  process: string | null;
  pid: number | null;
  event_type: string;
  outcome: SecurityEventOutcome;
  severity: SecurityEventSeverity;
  flagged: boolean;
  flag_reason: string | null;
  user_name: string | null;
  target_user: string | null;
  source_ip: string | null;
  source_port: number | null;
  auth_method: string | null;
  command: string | null;
  group_name: string | null;
  r2_record_index: number;
  received_at: number;
}

export interface SecurityEventQuery {
  page?: number;
  limit?: number;
  from?: number;
  to?: number;
  agent_id?: string;
  event_type?: string;
  severity?: SecurityEventSeverity;
  outcome?: SecurityEventOutcome;
  source_ip?: string;
  host?: string;
}

export interface SecurityEventList {
  events: SecurityEvent[];
  window: { from: number; to: number };
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
    has_more: boolean;
  };
  partial: boolean;
}

// Helper to build query string from params, filtering out undefined/null/empty values
function buildQueryString(params?: Record<string, any>): string {
  if (!params) return '';
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
  );
  const query = new URLSearchParams(filteredParams as Record<string, string>).toString();
  return query ? `?${query}` : '';
}

export class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string = API_URL) {
    this.baseUrl = baseUrl;
    // Load token from localStorage
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    // Always read token fresh from localStorage to handle cases where:
    // 1. Token was updated by Zustand auth store after ApiClient was instantiated
    // 2. Page was refreshed and Zustand hydrated asynchronously
    // 3. Token was refreshed/rotated in another tab
    const currentToken = this.token || localStorage.getItem('auth_token');
    if (currentToken) {
      headers['Authorization'] = `Bearer ${currentToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Auth
  async register(data: { email: string; password: string; organization_name: string }) {
    const response: any = await this.request('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (response.token) this.setToken(response.token);
    return response;
  }

  async login(data: { email: string; password: string }) {
    const response: any = await this.request('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (response.token) {
      this.setToken(response.token);
    }
    return response;
  }

  getSsoStartUrl(returnTo = '/') {
    return `${this.baseUrl}/api/v1/auth/sso/start${buildQueryString({ return_to: returnTo })}`;
  }

  async completeSso(code: string): Promise<{
    token: string;
    user: { id: string; email: string; role: string; organization_id: string };
    return_to: string;
  }> {
    return this.request('/api/v1/auth/sso/complete', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async rotateApiKey(name?: string) {
    return this.request('/api/v1/auth/api-key/rotate', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async getApiKeys(): Promise<{ api_keys: ManagementApiKey[] }> {
    return this.request('/api/v1/auth/api-keys');
  }

  async revokeApiKey(apiKeyId: string) {
    return this.request('/api/v1/auth/api-key/revoke', {
      method: 'POST',
      body: JSON.stringify({ api_key_id: apiKeyId }),
    });
  }

  async getCapabilities(): Promise<RuntimeCapabilities> {
    return this.request('/api/v1/capabilities');
  }

  // Node v1 projects and connection keys
  async getProjects(): Promise<{ projects: Project[] }> {
    return this.request('/api/v1/projects');
  }

  async createProject(data: {
    name: string;
    slug?: string;
    platform?: string;
    retention_days?: number;
  }): Promise<{ project: Project; dsn: string }> {
    return this.request('/api/v1/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProject(id: string) {
    return this.request(`/api/v1/projects/${id}`);
  }

  async createProjectKey(id: string, name?: string) {
    return this.request(`/api/v1/projects/${id}/keys`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async revokeProjectKey(projectId: string, keyId: string) {
    return this.request(`/api/v1/projects/${projectId}/keys/${keyId}`, {
      method: 'DELETE',
    });
  }

  async archiveProject(id: string) {
    return this.request(`/api/v1/projects/${id}`, { method: 'DELETE' });
  }

  // Version-scoped LogLimit transmission control
  async getTransmissionApplications(): Promise<{ applications: TransmissionApplication[] }> {
    return this.request('/api/v1/applications');
  }

  async createTransmissionApplication(data: {
    project_id: string;
    application_id: string;
    name: string;
    platform: string;
  }): Promise<{ application: TransmissionApplication }> {
    return this.request('/api/v1/applications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTransmissionApplication(applicationId: string): Promise<{
    application: TransmissionApplication;
    releases: TransmissionRelease[];
  }> {
    return this.request(`/api/v1/applications/${encodeURIComponent(applicationId)}`);
  }

  async getTransmissionCallsites(applicationId: string, version: string): Promise<{
    release: TransmissionRelease;
    callsites: TransmissionCallsite[];
  }> {
    return this.request(
      `/api/v1/applications/${encodeURIComponent(applicationId)}/releases/${encodeURIComponent(version)}/callsites`
    );
  }

  async getTransmissionAudit(applicationId: string, version: string): Promise<{
    release: TransmissionRelease;
    audit: TransmissionAuditEntry[];
  }> {
    return this.request(
      `/api/v1/applications/${encodeURIComponent(applicationId)}/releases/${encodeURIComponent(version)}/audit`
    );
  }

  async decideTransmissionRecommendation(
    applicationId: string,
    version: string,
    recommendationId: string,
    data: {
      action: 'approve_suppression' | 'approve_allow' | 'reject' | 'dismiss';
      reason: string;
      acknowledge_high_severity?: boolean;
    }
  ) {
    return this.request(
      `/api/v1/applications/${encodeURIComponent(applicationId)}/releases/${encodeURIComponent(version)}/recommendations/${encodeURIComponent(recommendationId)}/decision`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  async analyzeTransmissionRecommendations(applicationId: string, version: string): Promise<{
    recommendations: Array<{ id: string }>;
    candidates_analyzed: number;
    model_version?: string;
  }> {
    return this.request(
      `/api/v1/applications/${encodeURIComponent(applicationId)}/releases/${encodeURIComponent(version)}/recommendations/analyze`,
      { method: 'POST' }
    );
  }

  async setTransmissionDecision(
    applicationId: string,
    version: string,
    callsiteId: string,
    data: { action: 'suppress' | 'allow'; reason: string; acknowledge_high_severity?: boolean }
  ) {
    return this.request(
      `/api/v1/applications/${encodeURIComponent(applicationId)}/releases/${encodeURIComponent(version)}/callsites/${encodeURIComponent(callsiteId)}/decision`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  async revokeTransmissionDecision(
    applicationId: string,
    version: string,
    callsiteId: string,
    reason: string
  ) {
    return this.request(
      `/api/v1/applications/${encodeURIComponent(applicationId)}/releases/${encodeURIComponent(version)}/callsites/${encodeURIComponent(callsiteId)}/decision`,
      { method: 'DELETE', body: JSON.stringify({ reason }) }
    );
  }

  // Users
  async getUsers(params?: { page?: number; limit?: number; search?: string; role?: string }) {
    return this.request(`/api/v1/users${buildQueryString(params)}`);
  }

  async createUser(data: { email: string; password: string; role: string }) {
    return this.request('/api/v1/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: { email?: string; role?: string }) {
    return this.request(`/api/v1/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string) {
    return this.request(`/api/v1/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Config
  async getConfig(): Promise<OrganizationConfig> {
    return this.request('/api/v1/config');
  }

  async updateConfig(data: OrganizationConfig) {
    return this.request('/api/v1/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getNotificationChannels(): Promise<NotificationChannelsResponse> {
    return this.request('/api/v1/notification-channels');
  }

  async saveTopicBellChannel(data: {
    display_name: string;
    topic: string;
    token?: string;
    event_types: NotificationEventType[];
    enabled: boolean;
  }): Promise<{ channel: NotificationChannel }> {
    return this.request('/api/v1/notification-channels/topicbell', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async testTopicBellChannel(): Promise<{ ok: true; status: number; message_id: string | null; verified_at: number }> {
    return this.request('/api/v1/notification-channels/topicbell/test', { method: 'POST' });
  }

  async deleteTopicBellChannel(): Promise<{ success: true }> {
    return this.request('/api/v1/notification-channels/topicbell', { method: 'DELETE' });
  }

  async approveSecurityDevice(userCode: string): Promise<SecurityDeviceAuthorizationResult> {
    return this.request('/api/v1/security-agents/device/approve', {
      method: 'POST',
      body: JSON.stringify({ user_code: userCode }),
    });
  }

  async denySecurityDevice(userCode: string): Promise<SecurityDeviceAuthorizationResult> {
    return this.request('/api/v1/security-agents/device/deny', {
      method: 'POST',
      body: JSON.stringify({ user_code: userCode }),
    });
  }

  async getSecurityOverview(): Promise<SecurityOverview> {
    return this.request('/api/v1/security/overview');
  }

  async getSecurityEvents(params: SecurityEventQuery = {}): Promise<SecurityEventList> {
    return this.request(`/api/v1/security/events${buildQueryString(params)}`);
  }

  async getSecurityIncident(incidentId: string): Promise<SecurityIncidentDetail> {
    return this.request(`/api/v1/security/incidents/${encodeURIComponent(incidentId)}`);
  }

  // Billing
  async getBillingUsage() {
    return this.request('/api/v1/billing/usage');
  }

  // Logs
  async queryRecentLogs(data: {
    project_id?: string;
    application_id?: string;
    version?: string;
    levels?: TransmissionLogLevel[];
    service?: string;
    callsite_id?: string;
    search?: string;
    since_minutes?: number;
    limit?: number;
    offset?: number;
    cursor?: string;
    include_attributes?: boolean;
  }): Promise<RecentTransmissionLogsResponse> {
    return this.request('/api/v1/logs/recent', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Hosted MCP access
  async getMcpAccessTokens(): Promise<{ tokens: McpAccessToken[] }> {
    return this.request('/api/v1/mcp-access');
  }

  async createMcpAccessToken(data: {
    name: string;
    preset: 'observe' | 'publish' | 'manage';
    expires_in_days?: number | null;
  }): Promise<CreatedMcpAccess> {
    return this.request('/api/v1/mcp-access', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeMcpAccessToken(id: string) {
    return this.request(`/api/v1/mcp-access/${id}`, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
